import { jsPDF } from "jspdf";
import {
  TEXT_X_MM, TEXT_WIDTH_MM, PAGE_HEIGHT_MM,
  mmToPt, drawHeader, drawFooter, ensureSpace, getTextRegion, drawRichText,
} from "./patientInfoLayout";
import { renderMarkdown } from "@emr/shared/markdown";

// 2026-11-01, Batch 7 Wave 2 dedupe — drawRichText + stripTags were extracted
// verbatim to ./layoutShared.ts (re-exported through this renderer's layout
// sibling, patientInfoLayout) — byte-identical (modulo the export keyword) with
// the copies inlined in referralPdf.ts / certificatePdf.ts (the h1 heading
// size differed only in certificatePdf.ts; this call site passes its previous
// literal 14 — see layoutShared.ts's drawRichText doc).

export interface PatientInfoTemplatePdf {
  id: string;
  title: string;
  content: string | null;
}

export interface PatientInfoPdfData {
  templates: PatientInfoTemplatePdf[];
  patientName: string | null;
  clinicUhid: string | null;
  patientDob: string | null;
  patientGender: "Male" | "Female" | "Other" | null;
}

export async function createPatientInfoPacketPdf(data: PatientInfoPdfData): Promise<jsPDF> {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  await drawHeader(doc);
  await drawFooter(doc);

  const region = getTextRegion(doc);
  let y = region.y;
  const x = region.x;
  const w = region.width;
  let firstPage = true;

  // Patient info bar on first page only.
  if (data.patientName) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(26, 20, 19);
    doc.text(`Patient: ${data.patientName}`, x, y);
    if (data.clinicUhid) {
      doc.setFont("helvetica", "normal");
      doc.text(`UHID: ${data.clinicUhid}`, x + w, y, { align: "right" });
    }
    y += mmToPt(5);
    y += mmToPt(3);
  }

  for (let i = 0; i < data.templates.length; i++) {
    const tpl = data.templates[i];

    if (i > 0) {
      // Page break between templates.
      doc.addPage();
      await drawHeader(doc);
      await drawFooter(doc);
      y = region.y;
      firstPage = false;
    }

    // Template title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(124, 45, 18);
    const titleLines = doc.splitTextToSize(tpl.title, w);
    for (const line of titleLines) {
      y = await ensureSpace(doc, y, mmToPt(6));
      doc.text(line, x, y);
      y += mmToPt(6);
    }
    y += mmToPt(2);

    // Separator line
    doc.setDrawColor(200, 190, 180);
    y = await ensureSpace(doc, y, mmToPt(2));
    doc.line(x, y, x + w, y);
    y += mmToPt(4);

    // Content
    if (tpl.content) {
      const html = renderMarkdown(tpl.content);
      y = await drawRichText(doc, y, x, w, html, 14);
    }
  }

  return doc;
}
